import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Pure part of the fit: the largest size in [min, max] whose measured width
 * fits `available`. Text width scales linearly with font size, so one
 * measurement is enough -- no iterative shrinking needed.
 *
 * Exported for testing; the DOM measuring lives in the hook below.
 *
 * @param {number} measuredWidth width of the text at `maxSize`
 * @param {number} available     width the text has to fit into
 * @param {number} maxSize       preferred size, used when the text already fits
 * @param {number} minSize       floor, so the label never becomes unreadable
 */
export const fitSize = (measuredWidth, available, maxSize, minSize) => {
  if (!measuredWidth || !available || available <= 0) return maxSize;
  if (measuredWidth <= available) return maxSize;
  // shave a hair off so sub-pixel rounding can't push it back to two lines
  const scaled = maxSize * (available / measuredWidth) * 0.98;
  return Math.max(minSize, Math.min(maxSize, scaled));
};

/**
 * Shrinks an element's font size just enough to keep its text on one line.
 *
 * Used for the stat-card subtitles, where labels vary in length but the cards
 * must stay the same height. The element needs `white-space: nowrap` for the
 * measurement to mean anything -- see `subtitleSingleLine` in
 * assets/theme/components/card-stats.js.
 *
 * @param {string} text     re-fits when the label changes
 * @param {number} maxSize  preferred font size in rem
 * @param {number} minSize  smallest acceptable font size in rem
 */
const useShrinkToFit = (text, maxSize = 0.8125, minSize = 0.5625) => {
  const ref = useRef(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el || !el.parentElement) return;

    // measure at full size, then scale down in one step
    el.style.fontSize = maxSize + "rem";
    const available = el.parentElement.clientWidth;
    let size = fitSize(el.scrollWidth, available, maxSize, minSize);
    el.style.fontSize = size + "rem";

    // Verify rather than trust the estimate. Letter-spacing and sub-pixel
    // rounding mean width isn't perfectly linear in font size, and a stale
    // first measurement (fallback font, card not laid out yet) can report a
    // fit that isn't one. Step down until it genuinely fits.
    let guard = 0;
    while (el.scrollWidth > el.clientWidth && size > minSize && guard < 16) {
      size = Math.max(minSize, size - 0.03125);
      el.style.fontSize = size + "rem";
      guard += 1;
    }
  }, [maxSize, minSize]);

  useLayoutEffect(fit, [fit, text]);

  useEffect(() => {
    // The first measurement happens while the fallback font is still in use.
    // Open Sans is wider, so text that "fit" at mount overflows once the swap
    // lands -- and since the column width never changes, nothing else would
    // trigger a re-measure. This is the case that leaves a label clipped.
    let cancelled = false;
    if (typeof document !== "undefined" && document.fonts) {
      document.fonts.ready.then(() => {
        if (!cancelled) fit();
      });
    }
    return () => {
      cancelled = true;
    };
  }, [fit]);

  useEffect(() => {
    // refit when the card is resized by a viewport or sidebar change
    if (typeof ResizeObserver === "undefined") return undefined;
    const el = ref.current;
    if (!el || !el.parentElement) return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(el.parentElement);
    return () => observer.disconnect();
  }, [fit]);

  useEffect(() => {
    // belt and braces: ResizeObserver misses some reflows (zoom, font-size
    // preference changes) that a window resize does surface
    if (typeof window === "undefined") return undefined;
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [fit]);

  return ref;
};

export default useShrinkToFit;
